import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { zIndex } from "../config/zIndex";

const PLACEMENTS = {
	bottom: css`
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		margin-top: 4px;
	`,
	top: css`
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		margin-bottom: 4px;
	`,
	right: css`
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 4px;
	`,
	left: css`
		right: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-right: 4px;
	`,
};

const GAP = 4;

// Choose a side where the bubble stays inside the viewport.
// icon = info icon's getBoundingClientRect(); size = tooltip width/height.
function pickPlacement(icon, size) {
	const vw = window.innerWidth;
	const vh = window.innerHeight;

	// Bottom/top placements are centered on the icon (translateX(-50%)).
	const midX = icon.left + (icon.width / 2);
	const centeredLeft = midX - (size.width / 2);
	const centeredRight = midX + (size.width / 2);
	const centerFitsX = (centeredLeft >= 0) && (centeredRight <= vw);

	// Vertical: bubble stacked under / above the icon, plus GAP.
	const fitsBottom = (icon.bottom + GAP + size.height) <= vh;
	const fitsTop = (icon.top - GAP - size.height) >= 0;
	// Horizontal: bubble to the right / left of the icon, plus GAP.
	const fitsRight = (icon.right + GAP + size.width) <= vw;
	const fitsLeft = (icon.left - GAP - size.width) >= 0;

	// Prefer below the icon; last-column cells usually fail centerFitsX.
	if (fitsBottom && centerFitsX) {
		return "bottom";
	}
	if (fitsTop && centerFitsX) {
		return "top";
	}
	if (fitsLeft) {
		return "left";
	}
	if (fitsRight) {
		return "right";
	}

	// Nothing fully fits (tiny window): use the side with the most leftover pixels.
	const space = {
		bottom: vh - icon.bottom,
		top: icon.top,
		left: icon.left,
		right: vw - icon.right,
	};
	return Object.entries(space).sort((a, b) => b[1] - a[1])[0][0];
}

const TooltipText = styled.span`
	visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
	width: max-content;
	background-color: darkgray;
	color: #fff;
	text-align: center;
	border-radius: 6px;
	padding: 5px 10px;
	font-size: 14px;
	white-space: pre-line;
	position: absolute;
	z-index: ${zIndex.tooltip};
	${(props) => PLACEMENTS[props.$placement] || PLACEMENTS.bottom}
`;

const TooltipWrap = styled.div`
	position: relative;
	display: inline-block;
	cursor: pointer;
`;

const Tooltip = ({ text, placement = "auto" }) => {
	const isAuto = placement === "auto";
	const wrapRef = useRef(null);
	const textRef = useRef(null);
	const [resolvedPlacement, setResolvedPlacement] = useState(isAuto ? "bottom" : placement);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (!isAuto) {
			setResolvedPlacement(placement);
		}
	}, [isAuto, placement]);

	function show() {
		if (isAuto && wrapRef.current && textRef.current) {
			const icon = wrapRef.current.getBoundingClientRect();
			const bubble = textRef.current.getBoundingClientRect();
			if (bubble.width && bubble.height) {
				setResolvedPlacement(pickPlacement(icon, { width: bubble.width, height: bubble.height }));
			}
		}
		setVisible(true);
	}

	function hide() {
		setVisible(false);
	}

	return (
		<TooltipWrap ref={wrapRef} onMouseEnter={show} onMouseLeave={hide}>
			<span
				style={{ fontSize: "12px", color: "rgba(0,0,0,1)" }}
				className="material-symbols-outlined">
				info
			</span>
			<TooltipText ref={textRef} $placement={resolvedPlacement} $visible={visible}>
				{text}
			</TooltipText>
		</TooltipWrap>
	);
};

export default Tooltip;
